import React from "react";
import { useTranslation } from "react-i18next";
import { CardContent } from "@/components/ui/card";
import { StepIndicator } from "../../components/silae/StepIndicator";
import { WizardFooter } from "../../components/silae/WizardFooter";

// Import des étapes
import { Step1StructureChoice } from "../../components/silae/wizard-code-generator/Step1StructureChoice";
import { Step2Conditions } from "../../components/silae/wizard-code-generator/Step2Conditions";
import { Step3ConfigurationIF } from "../../components/silae/wizard-code-generator/Step3ConfigurationIF";
import { Step4ConfigurationElse } from "../../components/silae/wizard-code-generator/Step4ConfigurationElse";
import { Step5Validation } from "../../components/silae/wizard-code-generator/Step5Validation";
import { Step6Generation } from "../../components/silae/wizard-code-generator/Step6Generation";

// Hook personnalisé pour le wizard
import {
  useSilaeWizardFormik,
  createEmptyBonus,
} from "@/hooks/useSilaeWizardFormik";
import { useWizardStepTitles } from "@/hooks/useWizardSteps";
import {
  updateGeneratedCode,
  getGeneratedCodeById,
} from "@/services/generatedCodeService";
import { toast } from "sonner";
import { useEffect, useState } from "react";
import { useAppSelector, useAppDispatch } from "@/hooks/redux";
import {
  setCurrentStep,
  setFormData as setReduxFormData,
} from "@/store/slices/codeGenerationSlice";
import type { SilaeFormData } from "@/types/silae";

interface CodeMeta {
  name: string;
  description: string | null;
  tags: string[];
  createdAt?: string | null;
  updatedAt?: string | null;
}

export interface WizardStep {
  id: string;
  title: string;
  description: string;
  isCompleted: boolean;
  isActive: boolean;
  isAccessible: boolean;
}

/**
 * Composant principal du wizard SILAE multi-étapes
 * Gère la navigation et la progression entre les étapes
 */
export interface SilaeCodeWizardProps {
  codeId?: string | null;
  /** Si true, ouvre directement à l'étape Validation (avant-dernière) */
  openAtValidation?: boolean;
  onCodeSaved?: () => void;
  onFinish?: () => void;
  onCancel?: () => void;
}

/** Returns the 0-based Redux step index for the Validation step given a structureType */
function getValidationStep(structureType: string): number {
  switch (structureType) {
    case "if_else":
      return 4; // totalSteps=6 → validation is step 5 (1-based) → 4 (0-based)
    case "with_conditions":
      return 3; // totalSteps=5 → validation is step 4 (1-based) → 3 (0-based)
    case "simple":
    default:
      return 2; // totalSteps=4 → validation is step 3 (1-based) → 2 (0-based)
  }
}

export const SilaeCodeWizard: React.FC<SilaeCodeWizardProps> = ({
  codeId,
  openAtValidation = false,
  onCodeSaved,
  onFinish,
  onCancel,
}) => {
  const { t } = useTranslation();
  const { user } = useAppSelector((state) => state.auth);
  const dispatch = useAppDispatch();
  const [isSaving, setIsSaving] = useState(false);
  const [isLoadingCode, setIsLoadingCode] = useState(false);
  const [codeMeta, setCodeMeta] = useState<CodeMeta | null>(null);

  const {
    currentStep,
    totalSteps,
    structureType,
    formik,
    generatedCode,
    isGenerating,
    goToNextStep,
    goToPreviousStep,
    hasCurrentStepErrors,
    handleCopyCode,
    handleDownloadCode,
    handleEditCode,
    resetWizard,
    loadFormData,
  } = useSilaeWizardFormik();

  // Hooks pour les titres et descriptions dynamiques
  const stepTitles = useWizardStepTitles(structureType);

  // Load existing code if codeId is provided
  useEffect(() => {
    const loadCode = async () => {
      if (codeId && user?.id) {
        try {
          setIsLoadingCode(true);
          const code = await getGeneratedCodeById(codeId);
          setCodeMeta({
            name: code.name,
            description: code.description,
            tags: code.tags ?? [],
            createdAt: code.createdAt ?? null,
            updatedAt: code.updatedAt ?? null,
          });

          // Use Redux action to set form data (cast to SilaeFormData)
          const formDataToLoad = code.formData as unknown as SilaeFormData;

          // Le commentaire est stocké dans une colonne dédiée côté API
          // mais vit côté UI dans formData.comment pour cohérence Formik/Redux.
          formDataToLoad.comment = code.comment ?? "";

          // Ensure ifBranch has at least one bonus
          if (
            formDataToLoad.ifBranch &&
            (!formDataToLoad.ifBranch.bonuses ||
              formDataToLoad.ifBranch.bonuses.length === 0)
          ) {
            formDataToLoad.ifBranch.bonuses = [createEmptyBonus()];
          }

          // Ensure elseBranch has at least one bonus if it exists
          if (
            formDataToLoad.elseBranch &&
            (!formDataToLoad.elseBranch.bonuses ||
              formDataToLoad.elseBranch.bonuses.length === 0)
          ) {
            formDataToLoad.elseBranch.bonuses = [createEmptyBonus()];
          }

          // Push data into Redux (source of truth) and reinitialize Formik
          // in one atomic step so the wizard never shows stale values.
          dispatch(setReduxFormData(formDataToLoad));
          const targetStep = openAtValidation
            ? getValidationStep(formDataToLoad.structureType)
            : 0;
          dispatch(setCurrentStep(targetStep));
          loadFormData(formDataToLoad);
        } catch (error) {
          console.error("❌ Failed to load generated code for editing:", error);
          toast.error(t("codeGenerator.errors.loadCodeFailed"));
        } finally {
          setIsLoadingCode(false);
        }
      }
    };
    loadCode();
  }, [codeId, user?.id, dispatch, t, loadFormData, openAtValidation]);

  // Handle saving the code before finishing
  const handleSaveAndFinish = async () => {
    if (!codeId || !user?.id) {
      // If no codeId, just finish without saving (it's a new code that should have been created already)
      if (onFinish) {
        onFinish();
      }
      return;
    }

    try {
      setIsSaving(true);
      const formValues = formik.values as SilaeFormData;
      // Le commentaire est persisté dans une colonne dédiée (top-level)
      // pour éviter la duplication avec le jsonb formData.
      const { comment, ...formDataWithoutComment } = formValues;
      await updateGeneratedCode(codeId, {
        formData: formDataWithoutComment as SilaeFormData,
        generatedCode: generatedCode,
        comment: typeof comment === "string" ? comment : null,
      });
      toast.success(t("codeGenerator.messages.codeUpdated"));

      if (onCodeSaved) {
        onCodeSaved();
      }

      if (onFinish) {
        onFinish();
      }
    } catch (error: unknown) {
      console.error("Failed to save generated code:", error);
      const errorMessage =
        error instanceof Error ? error.message : t("common.error");
      toast.error(
        t("codeGenerator.errors.saveFailed", {
          error: errorMessage,
        }),
      );
    } finally {
      setIsSaving(false);
    }
  };

  // Handle save and quit (same as save and finish)
  const handleSaveAndQuit = async () => {
    await handleSaveAndFinish();
  };

  // Handle next step with finish logic
  const handleNextStep = async () => {
    // If we're on the last step, save and finish
    if (currentStep === totalSteps) {
      await handleSaveAndFinish();
      return;
    }

    // Otherwise, proceed to next step normally
    await goToNextStep();
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return <Step1StructureChoice formik={formik} />;
      case 2:
        if (structureType === "simple") {
          return <Step3ConfigurationIF formik={formik} />;
        }
        return <Step2Conditions formik={formik} />;
      case 3:
        if (structureType === "simple") {
          return (
            <Step5Validation
              formik={formik}
              codeMeta={codeMeta}
              codeId={codeId}
            />
          );
        }
        return <Step3ConfigurationIF formik={formik} />;
      case 4:
        if (structureType === "simple") {
          return (
            <Step6Generation
              formik={formik}
              generatedCode={generatedCode}
              isGenerating={isGenerating}
              onGenerate={() => {
                formik.handleSubmit();
              }}
              onCopy={handleCopyCode}
              onDownload={handleDownloadCode}
              onEdit={handleEditCode}
            />
          );
        }
        if (structureType === "if_else") {
          return <Step4ConfigurationElse formik={formik} />;
        }
        return (
          <Step5Validation
            formik={formik}
            codeMeta={codeMeta}
            codeId={codeId}
          />
        );
      case 5:
        if (structureType === "if_else") {
          return (
            <Step5Validation
              formik={formik}
              codeMeta={codeMeta}
              codeId={codeId}
            />
          );
        }
        return (
          <Step6Generation
            formik={formik}
            generatedCode={generatedCode}
            isGenerating={isGenerating}
            onGenerate={() => formik.handleSubmit()}
            onCopy={handleCopyCode}
            onDownload={handleDownloadCode}
            onEdit={handleEditCode}
          />
        );
      case 6:
        return (
          <Step6Generation
            formik={formik}
            generatedCode={generatedCode}
            isGenerating={isGenerating}
            onGenerate={() => formik.handleSubmit()}
            onCopy={handleCopyCode}
            onDownload={handleDownloadCode}
            onEdit={handleEditCode}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="flex flex-col h-[calc(100vh-56px)] max-h-[calc(100vh-60px)] ">
      {/* Header fixe avec titre et step indicator */}
      <div className="shrink-0 space-y-6">
        {/* Step indicator */}
        <StepIndicator
          currentStep={currentStep}
          totalSteps={totalSteps}
          getStepTitle={(step: number) => {
            return (
              stepTitles[step - 1] ||
              `${t("wizard.stepIndicator.step")} ${step}`
            );
          }}
          onReset={resetWizard}
          onCancel={onCancel}
          onSaveAndQuit={handleSaveAndQuit}
          isSaveDisabled={isSaving || isLoadingCode}
        />
      </div>

      {/* Body scrollable dans une seule carte */}
      <CardContent
        className="flex-1 overflow-y-auto p-6 "
        style={{
          scrollbarWidth: "thin",
          scrollbarColor: "rgba(156, 163, 175, 0.4) transparent",
        }}
      >
        {isLoadingCode ? (
          <div className="flex items-center justify-center h-full">
            <div className="flex flex-col items-center gap-4">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
              <p className="text-muted-foreground">{t("common.loading")}</p>
            </div>
          </div>
        ) : (
          renderStepContent()
        )}
      </CardContent>

      {/* Footer fixe avec navigation */}
      <WizardFooter
        currentStep={currentStep}
        totalSteps={totalSteps}
        wizardStructure={structureType}
        onPreviousStep={goToPreviousStep}
        onNextStep={handleNextStep}
        isNextDisabled={hasCurrentStepErrors() || isSaving || isLoadingCode}
      />
    </div>
  );
};
